/**
 * POS Quick Actions Panel
 * Slide-in side panel from the RIGHT with all quick actions
 * Fixed trigger button always visible on the right side of the screen
 */
import { 
  LockOpen, RotateCcw, Undo2, Receipt, GraduationCap, 
  FileText, Wallet, Loader2, ChevronLeft, ChevronRight, Printer,
  Table, User, Tag, AlertTriangle, MonitorSmartphone, Users, X, Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function POSQuickActions({
  showQuickActions,
  setShowQuickActions,
  quickActionLoading,
  onOpenDrawer,
  onPrintLastReceipt,
  onTestPrint,
  onVoidInvoice,
  onRefund,
  onAdvanceSale,
  onProformaSale,
  isTrainingMode,
  onToggleTrainingMode,
  isOwnerOrder,
  onToggleOwnerOrder,
  lastOrder,
  staff,
  // New action handlers
  onFindTableBill,
  onStaffOrder,
  onPromoCode,
  onLogMistake,
  onKioskOrders,
  onFamilyFriendsDiscount,
  kioskOrderCount = 0
}) {
  // Check if staff has drawer permission
  const canOpenDrawer = staff?.permissions?.can_open_drawer || staff?.role !== 'staff';

  return (
    <>
      {/* Fixed Trigger Button on Right Side */}
      <button
        onClick={() => setShowQuickActions(!showQuickActions)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-1 px-2 py-4 rounded-l-xl shadow-lg transition-all duration-300 ${
          showQuickActions 
            ? 'bg-teal-600 text-white' 
            : 'bg-white dark:bg-gray-800 text-teal-600 dark:text-teal-400 border border-r-0 border-gray-200 dark:border-gray-700'
        }`}
        title="Quick Actions"
        data-testid="pos-quick-actions-toggle"
      >
        {showQuickActions ? (
          <ChevronRight className="w-5 h-5" />
        ) : (
          <>
            <Settings className="w-5 h-5" />
            <ChevronLeft className="w-4 h-4" />
          </>
        )}
      </button>

      {/* Slide-in Panel from Right */}
      <div 
        className={`fixed right-0 top-0 h-full w-72 bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          showQuickActions ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Panel Header */}
        <div className="sticky top-0 bg-teal-600 text-white p-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-lg">Quick Actions</h2>
          <button
            onClick={() => setShowQuickActions(false)}
            className="p-1 hover:bg-teal-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Actions Grid */}
        <div className="p-4 grid grid-cols-2 gap-3">
          {/* Test Print ALL Printers - PROMINENT */}
          <Button
            variant="outline"
            size="sm"
            onClick={onTestPrint}
            disabled={quickActionLoading === 'testprint'}
            className="flex flex-col items-center gap-1 h-auto py-4 col-span-2 bg-green-50 dark:bg-green-900/30 text-green-700 hover:text-green-800 dark:text-green-300 border-green-300 hover:border-green-400"
            data-testid="pos-test-print"
          >
            {quickActionLoading === 'testprint' ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <Printer className="w-6 h-6" />
            )}
            <span className="text-sm font-medium">Test All Printers</span>
            <span className="text-xs text-green-600 dark:text-green-400">Customer + Kitchen + Barista + Juicer + Counter</span>
          </Button>

          {/* Open Drawer */}
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenDrawer}
            disabled={quickActionLoading === 'drawer' || !canOpenDrawer}
            className="flex flex-col items-center gap-1 h-auto py-3"
            data-testid="pos-open-drawer"
            title={!canOpenDrawer ? "You don't have permission to open the drawer" : "Open cash drawer"}
          >
            {quickActionLoading === 'drawer' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <LockOpen className="w-5 h-5" />
            )}
            <span className="text-xs">Open Drawer</span>
          </Button>

          {/* Print Last Receipt */}
          <Button
            variant="outline"
            size="sm"
            onClick={onPrintLastReceipt}
            disabled={!lastOrder || quickActionLoading === 'print'}
            className="flex flex-col items-center gap-1 h-auto py-3"
            data-testid="pos-print-last"
          >
            {quickActionLoading === 'print' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Receipt className="w-5 h-5" />
            )}
            <span className="text-xs">Last Receipt</span>
          </Button>

          {/* Find Table Bill / Split Bill */}
          <Button
            variant="outline"
            size="sm"
            onClick={onFindTableBill}
            className="flex flex-col items-center gap-1 h-auto py-3 col-span-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 hover:text-indigo-800 dark:text-indigo-300 border-indigo-300"
            data-testid="pos-find-table-bill"
          >
            <Table className="w-5 h-5" />
            <span className="text-xs font-medium">Split / Pay Table Bill</span>
          </Button>

          {/* Void Invoice */}
          <Button
            variant="outline"
            size="sm"
            onClick={onVoidInvoice}
            className="flex flex-col items-center gap-1 h-auto py-3 text-amber-600 hover:text-amber-700 border-amber-200"
            data-testid="pos-void-invoice"
          >
            <RotateCcw className="w-5 h-5" />
            <span className="text-xs">Void Invoice</span>
          </Button>

          {/* Refund */}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefund}
            className="flex flex-col items-center gap-1 h-auto py-3 text-red-600 hover:text-red-700 border-red-200"
            data-testid="pos-refund"
          >
            <Undo2 className="w-5 h-5" />
            <span className="text-xs">Refund</span>
          </Button>

          {/* Staff 50% Discount */}
          <Button
            variant="outline"
            size="sm"
            onClick={onStaffOrder}
            className="flex flex-col items-center gap-1 h-auto py-3 text-purple-600 hover:text-purple-700 border-purple-200"
            data-testid="pos-staff-order"
          >
            <User className="w-5 h-5" />
            <span className="text-xs">Staff 50%</span>
          </Button>

          {/* Family & Friends Discount */}
          <Button
            variant="outline"
            size="sm"
            onClick={onFamilyFriendsDiscount}
            className="flex flex-col items-center gap-1 h-auto py-3 text-rose-600 hover:text-rose-700 border-rose-200"
            data-testid="pos-ff-discount"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs">F&F 15%</span>
          </Button>

          {/* Promo Code */}
          <Button
            variant="outline"
            size="sm"
            onClick={onPromoCode}
            className="flex flex-col items-center gap-1 h-auto py-3 text-green-600 hover:text-green-700 border-green-200"
            data-testid="pos-promo-code"
          >
            <Tag className="w-5 h-5" />
            <span className="text-xs">Promo Code</span>
          </Button>

          {/* Log Mistake */}
          <Button
            variant="outline"
            size="sm"
            onClick={onLogMistake}
            className="flex flex-col items-center gap-1 h-auto py-3 text-amber-600 hover:text-amber-700 border-amber-200"
            data-testid="pos-log-mistake"
          >
            <AlertTriangle className="w-5 h-5" />
            <span className="text-xs">Log Mistake</span>
          </Button>

          {/* Kiosk Orders */}
          <Button
            variant="outline"
            size="sm"
            onClick={onKioskOrders}
            className={`flex flex-col items-center gap-1 h-auto py-3 relative ${
              kioskOrderCount > 0 
                ? 'text-red-600 border-red-300 animate-pulse' 
                : 'text-cyan-600 border-cyan-200'
            }`}
            data-testid="pos-kiosk-orders"
          >
            <MonitorSmartphone className="w-5 h-5" />
            <span className="text-xs">Kiosk</span>
            {kioskOrderCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {kioskOrderCount}
              </span>
            )}
          </Button>

          {/* Owner Order Toggle - FULL WIDTH, PROMINENT */}
          <Button
            variant={isOwnerOrder ? "default" : "outline"}
            size="sm"
            onClick={onToggleOwnerOrder}
            className={`flex flex-col items-center gap-1 h-auto py-4 col-span-2 ${
              isOwnerOrder 
                ? 'bg-teal-600 hover:bg-teal-700 text-white border-teal-600' 
                : 'text-teal-600 hover:text-teal-700 border-teal-300 bg-teal-50'
            }`}
            data-testid="pos-owner-order"
          >
            <User className="w-6 h-6" />
            <span className="text-sm font-bold">{isOwnerOrder ? '👑 OWNER MODE ON' : '👑 Owner Order'}</span>
            <span className="text-xs opacity-80">{isOwnerOrder ? 'No charge - prints to stations' : 'Click for owner meal'}</span>
          </Button>

          {/* Advance Sale */}
          <Button
            variant="outline"
            size="sm"
            onClick={onAdvanceSale}
            className="flex flex-col items-center gap-1 h-auto py-3 text-blue-600 hover:text-blue-700 border-blue-200"
            data-testid="pos-advance-sale"
          >
            <Wallet className="w-5 h-5" />
            <span className="text-xs">Advance</span>
          </Button>

          {/* Proforma */}
          <Button
            variant="outline"
            size="sm"
            onClick={onProformaSale}
            className="flex flex-col items-center gap-1 h-auto py-3 text-purple-600 hover:text-purple-700 border-purple-200"
            data-testid="pos-proforma"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs">Proforma</span>
          </Button>

          {/* Training Mode Toggle - FULL WIDTH */}
          <Button
            variant={isTrainingMode ? "default" : "outline"}
            size="sm"
            onClick={onToggleTrainingMode}
            className={`flex flex-col items-center gap-1 h-auto py-3 col-span-2 ${
              isTrainingMode 
                ? 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500' 
                : 'text-yellow-600 hover:text-yellow-700 border-yellow-300'
            }`}
            data-testid="pos-training-mode"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs font-medium">{isTrainingMode ? 'Training Mode ON' : 'Enable Training Mode'}</span>
          </Button>
        </div>
      </div>

      {/* Backdrop when panel is open */}
      {showQuickActions && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setShowQuickActions(false)}
        />
      )}
    </>
  );
}
